/* Core & libraries */
import React, { useEffect } from "react";
import { useTheme } from "react-jss";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Image from "react-bootstrap/Image";
import moment from "helpers/moment.js";
import parse from "html-react-parser";


/* Helpers, constants, custom methods, internal libraries */
import { useAirlineProgramsService } from "../../services/airlineProgramsService.js";
import { useLocaleContext } from "../../contexts/localeContext.js";
import { AA_HOME_LINK } from "../../constants/appConstants";

/* Components */
import UCButton from "../../components/buttons";
import AmericanImageOne from "../../resources/images/aaThDesk.png";
import AmericanImageTwo from "../../resources/images/aaThMobile.png";
import CarouselImage2 from "../../resources/images/aaCarousel.png";
import CarouselImage9 from "../../resources/images/aaMobileCaro.png";
import ProgramCarouselModal from "../modals/programCarouselModal";

/* Styles */
import styles from "./styles.js";


const AAProgramContent = ({ delinkAmericanAirlines, americanAirlinesSupplier, joinAmericanAirlines }) => {

    const theme = useTheme();
    const classes = styles({ theme });
    const { getStaticContent, staticContent } = useAirlineProgramsService();
    const locale = useLocaleContext();

    const [showProgramCarousel, setShowProgramCarousel] = useState(false);
    const [showFullText, setShowFullText] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    const formatDate = (dateString) => {
        return moment(dateString).locale(locale).format("D MMM 'YY");
    };
    const handleShow = (index) => {
        setCurrentIndex(index);
        setShowProgramCarousel(true);
    };

    const handleSelect = (selectedIndex) => {
        setCurrentIndex(selectedIndex);
    };

    const images = [
        CarouselImage2, CarouselImage9
    ];

    useEffect(() => {
        if (!staticContent || !staticContent?.americanAirlines) {
            getStaticContent();
        }
    }, [staticContent]);

    const toggleText = () => {
        setShowFullText((prev) => !prev);
    };

    const staticContentUnavailable = Object.keys(staticContent).length === 0;
    const aaContentUnavailable = staticContent?.americanAirlines?.headerText === "" || staticContent?.americanAirlines?.text === ""

    const fullText = staticContent?.americanAirlines?.text || "";
    const maxVisibleChars = 490;

    let buttonInjected = false;

    const renderFormattedText = (text, truncate = false) => {
        const paragraphs = text.split("\n\n");
        let accumulatedLength = 0;
        let isTruncated = false;

        return paragraphs.map((paragraph, idx) => {
            const parsedParagraph = parse(paragraph);

            if (truncate && !isTruncated) {
                const remainingChars = maxVisibleChars - accumulatedLength;

                if (accumulatedLength + paragraph.length > maxVisibleChars) {
                    const truncatedContent = paragraph.slice(0, remainingChars) + "...";
                    isTruncated = true;

                    const truncatedParsedParagraph = parse(truncatedContent);

                    if (truncatedParsedParagraph.type === "ul") {
                        return (
                            <div key={idx} className={classes.customContentTextParagraph}>
                                {injectButtonInList(truncatedParsedParagraph)}
                            </div>
                        );
                    }

                    return (
                        <div key={idx} className={classes.customContentTextParagraph}>
                            {truncatedParsedParagraph}
                        </div>
                    );
                }
            }

            accumulatedLength += paragraph.length;
            return (
                <div
                    key={idx}
                    className={
                        idx === paragraphs.length - 1
                            ? classes.customContentTextLastParagraph
                            : classes.customContentTextParagraph
                    }
                >
                    {injectButtonInList(parsedParagraph)}
                </div>
            );
        });
    };

    const injectButtonInList = (element) => {
        if (!element || element.type !== "ul" || buttonInjected) {
            return element;
        }

        const children = React.Children.toArray(element.props.children);

        if (children.length > 0) {
            const lastChild = children[children.length - 1];

            if (lastChild.type === "li") {
                const newLastChild = React.cloneElement(lastChild, {
                    children: (
                        <>
                            {lastChild.props.children}
                            <button onClick={toggleText} className={classes.inlineReadMoreButton}>
                                <FormattedMessage id="airlineProgram.americanAirlines.program.readMoreDots" />
                                <FormattedMessage id="airlineProgram.americanAirlines.program.readMore" />
                            </button>
                        </>
                    ),
                });

                buttonInjected = true;
                return React.cloneElement(element, {
                    children: [...children.slice(0, -1), newLastChild],
                });
            }
        }

        return element;
    };

    return (
        <div className={classes.aaProgramContent}>
            <Col xs={12} className={classes.dividerContainer}>
                <hr className={classes.border} />
            </Col>
            {
                !staticContentUnavailable && !aaContentUnavailable && staticContent?.americanAirlines ? (
                    <Container className={classes.customContentContainer}>
                        <div>
                            <div className={classes.customContentHeader}>
                                {staticContent?.americanAirlines?.headerText}
                            </div>
                            <div className={classes.customContentText}>
                                {showFullText ? (
                                    renderFormattedText(fullText)
                                ) : (
                                    renderFormattedText(fullText.slice(0, maxVisibleChars), true)
                                )}

                                {!buttonInjected && (
                                    <button onClick={toggleText} className={classes.inlineReadMoreButton}>
                                        {showFullText ? (
                                            <FormattedMessage id="airlineProgram.americanAirlines.program.readLess" />
                                        ) : (
                                            <FormattedMessage id="airlineProgram.americanAirlines.program.readMore" />
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    </Container>
                ) : null
            }
            <Container className={classes.aaProgramContainer}>
                <Row className={classes.aaProgramRowOne}>
                    <Col className={classes.programWebsiteContainer}>
                        <p className={classes.bookOnText}><FormattedMessage id="airlineProgram.americanAirlines.program.bookOn" /></p>
                        <a href={AA_HOME_LINK} target="_blank" className={classes.programWebsiteText}><FormattedMessage id="airlineProgram.americanAirlines.program.website" /></a>
                    </Col>
                    {americanAirlinesSupplier && americanAirlinesSupplier?.userStatus !== "ACTIVE" && (
                        <UCButton
                            onClick={() => joinAmericanAirlines(americanAirlinesSupplier?.providerEntityID)}
                            type="primary"
                            className={classes.connectButton}
                            text={<FormattedMessage id="airlineProgram.americanAirlines.program.connectAccountButton" />}
                        />
                    )}
                    {americanAirlinesSupplier && americanAirlinesSupplier?.userStatus === "ACTIVE" && (
                        <>
                            <Col xs={5}>
                                <p className={classes.accountConnectedText}><FormattedMessage id="airlineProgram.americanAirlines.program.accountConnected" /></p>
                                <Row xs={12} className={classes.accountDetailsContainer}>
                                    <span className={classes.programAccountNumberText}>{americanAirlinesSupplier?.membershipNumber}</span><span className={classes.programAccountConnectedDate}>{formatDate(americanAirlinesSupplier?.dateLinked)}</span>
                                </Row>
                            </Col>
                            <Col className={classes.connectButtonContainer} xs={3}>
                                <UCButton
                                    onClick={() => delinkAmericanAirlines(americanAirlinesSupplier?.providerEntityID)}
                                    type="link"
                                    className={classes.delinkButton}
                                    text={<FormattedMessage id="airlineProgram.americanAirlines.program.disconnectAccountButton" />}
                                    loadingStateStyling={classes.loadingState}
                                    spinnerClassName={classes.delinkSpinner}
                                    spinner={{
                                        name: "delinkAmericanSpinner",
                                        size: 20,
                                    }}
                                />
                            </Col>
                        </>
                    )}
                </Row>
                <Row className={classes.aaProgramRowTwo}>
                    <Col xs={6}>
                        <Image
                            onClick={() => handleShow(0)}
                            src={AmericanImageOne} />
                    </Col>
                    <Col xs={6}>
                        <Image
                            onClick={() => handleShow(1)}
                            src={AmericanImageTwo} />
                    </Col>
                </Row>
                <Row className={classes.aaProgramRowThree}>
                    <Col xs={12}>
                        <Row className={classes.faqContainer}>
                            <p className={classes.faqTitleText}><FormattedMessage id="airlineProgram.americanAirlines.program.whatDoINeed" /></p>
                            <p className={classes.faqDescriptionText}>
                                <FormattedMessage id="airlineProgram.americanAirlines.program.whatDoINeedDescription" />
                                <strong><FormattedMessage id="airlineProgram.americanAirlines.program.whatDoINeedLink" /></strong>
                            </p>
                        </Row>
                        <Row className={classes.faqContainer}>
                            <p className={classes.faqTitleText}><FormattedMessage id="airlineProgram.americanAirlines.program.howItWorks" /></p>
                            <p className={classes.faqDescriptionText}><FormattedMessage id="airlineProgram.americanAirlines.program.howItWorksDescription1" /></p>
                            <p className={classes.faqDescriptionText2}><FormattedMessage id="airlineProgram.americanAirlines.program.howItWorksDescription2" /></p>
                        </Row>
                    </Col>
                </Row>
            </Container>
            <ProgramCarouselModal
                show={showProgramCarousel}
                setShow={setShowProgramCarousel}
                providerAirline={"american"}
                currentIndex={currentIndex}
                setCurrentIndex={setCurrentIndex}
                handleSelect={handleSelect}
                images={images}
            />
        </div>
    );
};

export default AAProgramContent;